/**
 * Extract a user-friendly error message from an AppSync/Amplify GraphQL error.
 *
 * AppSync can return errors in several shapes:
 *  - Thrown error with `errors` array (Amplify v6 wraps GraphQL errors this way)
 *  - Thrown error with a plain `message` string
 *  - Response body with `errors` array (partial data + errors)
 *
 * For PermissionError / Access denied, returns a clear permission message.
 */
export function extractGraphQLError(err: any, fallback = 'An unexpected error occurred'): string {
  const errors: any[] = err?.errors ?? [];

  if (errors.length > 0) {
    const first = errors[0];
    const msg: string = first.message ?? '';
    const errorType: string = first.errorType ?? '';

    if (errorType === 'PermissionError' || msg.includes('Access denied')) {
      // Pull the required groups from the message if available
      const match = msg.match(/requires one of \[([^\]]+)\]/);
      const requiredRoles = match ? match[1].replace(/'/g, '') : null;
      return requiredRoles
        ? `You do not have permission to perform this action. Required role: ${requiredRoles}.`
        : 'You do not have permission to perform this action.';
    }

    return msg || fallback;
  }

  if (typeof err?.message === 'string' && err.message.length > 0) {
    return err.message;
  }

  return fallback;
}
